import React, { useEffect, useState, useCallback } from "react";
import baseUrl from "../../../../api/baseUrl";
import { useParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface Patient {
  regNo: string;
  fullName: string;
  address: string;
  nic: string;
  whatsapp: string;
  dob: string;
  _id: string;
}

function InvoicePatient({ formData, setformData, setdoctorid, doctorid }: any) {
  const { ID } = useParams();
  const [doctor, setdoctor] = useState<any[]>([]);
  const [page] = useState(1);
  const [data, setData] = useState<Patient[]>([]);
  const [search, setsearch] = useState("");
  const [doctorDropdownOpen, setDoctorDropdownOpen] = useState(false);

  // Fetch data and doctor list
  const fetchData = useCallback(async (search: string, pages: number) => {
    try {
      const [patientsResponse, doctorsResponse] = await Promise.all([
        baseUrl.get("patient/findPatientsInvoice", {
          headers: {
            status: true,
            page: pages,
            keyword: search,
          },
        }),
        baseUrl.get("/employee/findEmployee", {
          headers: {
            status: true,
          },
        }),
      ]);

      setData(patientsResponse.data.data);
      setdoctor(doctorsResponse.data.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchData(search, page);
  }, [fetchData, search, page]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearch = e.target.value;
    setsearch(newSearch);
    fetchData(newSearch, page);
  };

  const onSetUser = (e: Patient) => {
    const birthdateDate = new Date(e.dob ?? new Date());
    const currentDate = new Date();
    const ageDiff = currentDate.getFullYear() - birthdateDate.getFullYear();
    const isBirthdayPassed =
      currentDate.getMonth() < birthdateDate.getMonth() ||
      (currentDate.getMonth() === birthdateDate.getMonth() &&
        currentDate.getDate() < birthdateDate.getDate());

    setformData({
      ...formData,
      userName: e.fullName,
      age: ageDiff - (isBirthdayPassed ? 1 : 0),
      regno: e.regNo,
      patientId: e._id,
    });
    setData([]);
    setsearch("");
  };

  return (
    <div className="space-y-3 mb-3">
      {!ID && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Search Patient */}
          <div className="relative">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Search Patient</label>
            <input
              type="search"
              onChange={onSearch}
              value={search}
              placeholder="Search by name or reg no..."
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-500 focus:outline-none transition-all text-xs bg-slate-50 dark:bg-slate-800 dark:text-slate-200"
            />
            {data.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-600 dark:border-slate-600 rounded-lg shadow-lg dark:shadow-slate-900/50 max-h-48 overflow-y-auto">
                {data.map((patient, index) => (
                  <li
                    key={index}
                    onClick={() => onSetUser(patient)}
                    className="px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 cursor-pointer flex items-center justify-between border-b border-slate-100 dark:border-slate-600 last:border-0"
                  >
                    <span className="text-xs font-medium text-slate-800 dark:text-slate-200">{patient.fullName}</span>
                    <span className="text-[10px] text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/40 px-2 py-0.5 rounded">{patient.regNo}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Select Dentist - Smart Dropdown */}
          <div className="relative">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Select Dentist</label>
            <button
              type="button"
              onClick={() => setDoctorDropdownOpen(!doctorDropdownOpen)}
              className="w-full px-3 py-2 rounded-lg border border-gray-600 dark:border-slate-500 border-solid bg-slate-50 dark:bg-slate-800 dark:text-slate-200 focus:outline-none transition-all cursor-pointer text-left flex items-center justify-between text-xs"
            >
              {doctorid ? (
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center text-white text-xs font-bold">
                    {doctor.find((d) => d._id === doctorid)?.firstName?.charAt(0) || "D"}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-800 dark:text-slate-200">
                      Dr. {doctor.find((d) => d._id === doctorid)?.firstName} {doctor.find((d) => d._id === doctorid)?.lastName}
                    </p>
                  </div>
                </div>
              ) : (
                <span className="text-xs text-slate-400 dark:text-slate-500">Select Dentist</span>
              )}
              <svg className={`w-4 h-4 text-slate-400 transition-transform ${doctorDropdownOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {doctorDropdownOpen && (
              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-xl overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {doctor.filter((d) => d.IsDentist).map((doc, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        setdoctorid(doc._id);
                        setDoctorDropdownOpen(false);
                      }}
                      className={`px-3 py-2 cursor-pointer flex items-center gap-2.5 transition-colors ${
                        doctorid === doc._id
                          ? "bg-primary/5 dark:bg-primary/20 border-l-4 border-primary"
                          : "hover:bg-slate-50 dark:hover:bg-slate-700/50 border-l-4 border-transparent"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                        doctorid === doc._id
                          ? "bg-gradient-to-br from-primary to-primaryLight"
                          : "bg-gradient-to-br from-slate-400 to-slate-500"
                      }`}>
                        {doc.firstName?.charAt(0)}{doc.lastName?.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                          Dr. {doc.firstName} {doc.lastName}
                        </p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">Dentist</p>
                      </div>
                      {doctorid === doc._id && (
                        <CheckCircleIcon className="text-primary" sx={{ fontSize: 16 }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Patient Details Card */}
      {formData?.userName && (
        <div className="bg-gradient-to-r from-primary to-primaryLight rounded-lg p-3 text-white">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <p className="text-[10px] text-white/80 uppercase tracking-wide">Patient Name</p>
              <p className="text-sm font-bold">{formData?.userName}</p>
            </div>
            <div>
              <p className="text-[10px] text-white/80 uppercase tracking-wide">Reg Number</p>
              <p className="text-sm font-bold">{formData?.regno}</p>
            </div>
            {formData?.age > 0 && (
              <div>
                <p className="text-[10px] text-white/80 uppercase tracking-wide">Age</p>
                <p className="text-sm font-bold">{formData?.age} years</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoicePatient;
